import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useApolloClient, useQuery } from '@apollo/react-hooks'
import { AsyncPaginate } from 'react-select-async-paginate'
import { components as RSComponents } from 'react-select'
import {
  Select as UiSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import * as Query from '../api/gql/query'

export interface SnagOption {
  value: string
  label: string
  infrastructureType?: string | null
}

interface BaseProps {
  projectId: string
  placeholder?: string
  className?: string
}

type SingleProps = BaseProps & {
  isMulti?: false
  value: SnagOption | null
  onChange: (value: SnagOption | null) => void
}

type MultiProps = BaseProps & {
  isMulti: true
  value: SnagOption[]
  onChange: (value: SnagOption[]) => void
}

type Props = SingleProps | MultiProps

const PAGE_SIZE = 30

export const snagOptionFromNode = (s: any): SnagOption => ({
  value: s.id,
  label: `${s.displayNumber || s.number || ''}${
    s.displayNumber || s.number ? ' · ' : ''
  }${s.title}`,
  infrastructureType: s.infrastructureType ?? null,
})

const SnagOptionItem = (props: any) => {
  const { data } = props
  return (
    <RSComponents.Option {...props}>
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span className="truncate">{data.label}</span>
        {data.infrastructureType && (
          <span className="shrink-0 text-xs rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
            {data.infrastructureType}
          </span>
        )}
      </div>
    </RSComponents.Option>
  )
}

const SnagLinkPicker: React.FC<Props> = (props) => {
  const { projectId, placeholder, className } = props
  const { t } = useTranslation()
  const client = useApolloClient()

  const [buildingId, setBuildingId] = useState<string>('')
  const [floorId, setFloorId] = useState<string>('')
  const [infrastructureType, setInfrastructureType] = useState<string>('')

  const { data: buildingsData } = useQuery(Query.BUILDINGS, {
    variables: { projectId },
    skip: !projectId,
  })
  const buildings = buildingsData?.buildings || []

  const { data: floorsData } = useQuery(Query.FLOORS, {
    variables: { buildingId },
    skip: !buildingId,
  })
  const floors = floorsData?.floors || []

  const { data: settingsData } = useQuery(Query.SNAG_SETTINGS, {
    variables: { projectId },
    skip: !projectId,
  })
  const infrastructureTypes: string[] =
    settingsData?.snagSettings?.infrastructureTypes || []

  // Async paginate loader — called by react-select-async-paginate with:
  //   search     : current input text
  //   _loaded    : previously loaded options (ignored, we track offset)
  //   additional : { offset } we thread through
  const loadOptions = async (
    search: string,
    _loaded: any,
    additional: { offset: number } | undefined
  ) => {
    const offset = additional?.offset ?? 0
    const filter: any = { projectId }
    if (buildingId) filter.buildingId = buildingId
    if (floorId) filter.floorId = floorId
    if (infrastructureType) filter.infrastructureType = [infrastructureType]
    if (search) filter.search = search

    try {
      const res = await client.query({
        query: Query.SNAGS,
        variables: { filter, limit: PAGE_SIZE, offset },
        fetchPolicy: 'network-only',
      })
      const options: SnagOption[] = (res.data?.snags || []).map(
        snagOptionFromNode
      )
      return {
        options,
        hasMore: options.length === PAGE_SIZE,
        additional: { offset: offset + options.length },
      }
    } catch {
      return {
        options: [],
        hasMore: false,
        additional: { offset },
      }
    }
  }

  const ASYNC_CLASSES = {
    container: () => 'w-full',
    control: ({ isFocused }: any) =>
      `flex min-h-9 w-full items-center rounded-md border bg-card pl-2 pr-1 text-sm transition-colors ${
        isFocused
          ? 'border-ring ring-2 ring-ring ring-offset-0'
          : 'border-input'
      }`,
    valueContainer: () => 'flex flex-wrap gap-1 py-1',
    placeholder: () => 'text-muted-foreground',
    singleValue: () => 'text-foreground',
    input: () =>
      'text-foreground m-0 py-0 [&>input]:bg-transparent [&>input]:border-0 [&>input]:outline-none [&>input]:shadow-none [&>input]:focus:ring-0',
    multiValue: () =>
      'inline-flex items-center gap-1 bg-muted rounded px-1.5 py-0.5 text-xs',
    multiValueLabel: () => 'leading-none',
    multiValueRemove: () =>
      'ml-0.5 hover:bg-muted-foreground/20 rounded cursor-pointer flex items-center',
    indicatorsContainer: () => 'flex items-center gap-0.5 shrink-0',
    clearIndicator: () =>
      'p-1 text-muted-foreground hover:text-foreground cursor-pointer',
    indicatorSeparator: () => 'hidden',
    dropdownIndicator: () =>
      'p-1 text-muted-foreground hover:text-foreground cursor-pointer',
    loadingIndicator: () => 'p-1 text-muted-foreground',
    menu: () =>
      'mt-1 overflow-hidden rounded-md border border-border bg-popover shadow-md z-[1200]',
    menuList: () => 'overflow-y-auto py-1',
    option: ({ isFocused, isSelected }: any) =>
      `px-3 py-2 text-sm cursor-pointer ${
        isSelected
          ? 'bg-primary text-primary-foreground'
          : isFocused
            ? 'bg-accent text-accent-foreground'
            : ''
      }`,
    noOptionsMessage: () => 'px-3 py-2 text-sm text-muted-foreground',
    loadingMessage: () => 'px-3 py-2 text-sm text-muted-foreground',
  }

  const commonProps = {
    loadOptions,
    debounceTimeout: 250,
    additional: { offset: 0 },
    // Re-fetch from scratch whenever any filter changes.
    cacheUniqs: [projectId, buildingId, floorId, infrastructureType],
    components: { Option: SnagOptionItem },
    placeholder: placeholder || t('SnagLinkPicker_placeholder'),
    noOptionsMessage: ({ inputValue }: any) =>
      inputValue
        ? t('SnagLinkPicker_no_match')
        : t('SnagLinkPicker_empty'),
    loadingMessage: () => t('Common_loading'),
    classNames: ASYNC_CLASSES as any,
    menuPlacement: 'auto' as const,
    maxMenuHeight: 280,
    unstyled: true,
    isClearable: true,
    isSearchable: true,
    closeMenuOnSelect: !props.isMulti,
    hideSelectedOptions: false,
  }

  return (
    <div className={className ?? 'space-y-2'}>
      <div className="flex gap-2 flex-wrap">
        <UiSelect
          value={buildingId || '__all__'}
          onValueChange={(v) => {
            setBuildingId(v === '__all__' ? '' : v)
            setFloorId('')
          }}
        >
          <SelectTrigger className="h-9 min-w-[140px] flex-1">
            <SelectValue placeholder={t('Snags_building')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">
              {t('Snags_building')} · {t('Common_all')}
            </SelectItem>
            {buildings.map((b: any) => (
              <SelectItem key={b.id} value={b.id}>
                {b.name}
              </SelectItem>
            ))}
          </SelectContent>
        </UiSelect>

        <UiSelect
          value={floorId || '__all__'}
          onValueChange={(v) => setFloorId(v === '__all__' ? '' : v)}
          disabled={!buildingId}
        >
          <SelectTrigger className="h-9 min-w-[140px] flex-1">
            <SelectValue placeholder={t('Snags_floor')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">
              {t('Snags_floor')} · {t('Common_all')}
            </SelectItem>
            {floors.map((f: any) => (
              <SelectItem key={f.id} value={f.id}>
                {f.name}
              </SelectItem>
            ))}
          </SelectContent>
        </UiSelect>

        {infrastructureTypes.length > 0 && (
          <UiSelect
            value={infrastructureType || '__all__'}
            onValueChange={(v) =>
              setInfrastructureType(v === '__all__' ? '' : v)
            }
          >
            <SelectTrigger className="h-9 min-w-[140px] flex-1">
              <SelectValue placeholder={t('Snags_field_infrastructure_type')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">
                {t('Snags_field_infrastructure_type')} · {t('Common_all')}
              </SelectItem>
              {infrastructureTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </UiSelect>
        )}
      </div>

      {props.isMulti ? (
        <AsyncPaginate
          {...commonProps}
          isMulti
          value={props.value}
          onChange={(opts: any) => props.onChange((opts as SnagOption[]) || [])}
        />
      ) : (
        <AsyncPaginate
          {...commonProps}
          value={props.value}
          onChange={(opt: any) => props.onChange((opt as SnagOption) || null)}
        />
      )}
    </div>
  )
}

export default SnagLinkPicker
